import { useEffect } from "react";

import ClientCreateForm from "./ClientCreateForm";

// ClientCreateModal — feature-local T-1 parent-owned modal.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-1
// ("modal triggered from parent header; mutation owns client core
// slice; success refetches parent on close"). Mirrors the existing
// ClientEditModal feature-local shape (see
// features/clientDetail/widgets/ClientEditModal.js) — same backdrop +
// dialog markup, no Bootstrap JS coupling, no shared modal host.
//
// Ownership:
//   * open/close state — owned by the parent
//     (ClientsListScreenContent) via plain useState; this widget
//     is fully controlled.
//   * mutation — owned by ClientCreateForm; this widget only relays
//     its onSuccess hand-off into onCreated.
//   * post-create freshness — owned by the parent (parent's
//     existing handleReload + close). No cross-section refresh,
//     no orchestration, no mayBeStale navigation (we never leave
//     the route).
//
// Effects in this primitive are limited to the allowed UI-local
// effects per CLAUDE.md "Presentation Primitive Rules": ESC-key
// handler and body scroll lock. No selectors, no dispatch, no
// imperative handle, no registry, no portal, no shared modal host.

function ClientCreateModal({ open, onClose, onCreated }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="feature-clients-list__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-create-modal-title"
    >
      <div
        className="feature-clients-list__modal-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="feature-clients-list__modal-dialog">
        <header className="feature-clients-list__modal-head">
          <h2
            id="client-create-modal-title"
            className="feature-clients-list__modal-title"
          >
            <i className="fa-solid fa-user-plus" aria-hidden="true" />
            <span>Новий клієнт</span>
          </h2>
          <button
            type="button"
            className="feature-clients-list__modal-close"
            onClick={onClose}
            aria-label="Закрити вікно створення"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-clients-list__modal-body">
          <ClientCreateForm
            onSuccess={onCreated}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
}

export default ClientCreateModal;
